// src/config/weapons.ts — missile dynamics and countermeasure behaviour (SPEC §3.6, §3.7).
// Distances metres, times seconds, forces newtons, angles degrees at this boundary only.

/**
 * Missile parameters (SPEC §6.3). The per-threat values live in `tuning.ts` alongside the sites
 * that fire them; this is the shape they take.
 */
export interface MissileParams {
  massKg: number;
  diameterM: number;
  boostThrustN: number;
  boostDurationS: number;
  sustainThrustN: number;
  sustainDurationS: number;
  cd0: number;
  /**
   * Induced drag coefficient, **per g² of commanded lateral acceleration**.
   *
   * The units matter and the spec does not state them, so here is the derivation that fixes
   * them. A SHORAD round at 600 m/s has a base drag of roughly 1900 N — about 2.6 g of
   * deceleration. For the induced term to be the mechanism SPEC §3.6.1 says it is ("every g the
   * missile pulls to follow costs it speed"), a maximum-g turn has to cost several times that,
   * not a rounding error and not an instant stop. Read per g², the catalogue value of 0.0021
   * gives ΔC_D = 1.65 at the 28 g limit against a C_D0 of 0.42 — total drag roughly quadruples
   * in a hard turn. Read per (m/s²)² it would give ΔC_D = 158, which stops the missile dead in
   * under a second and makes every intercept trivially survivable.
   */
  inducedDragK: number;
  maxG: number;
  navConstant: number;
  seeker: 'radar-command' | 'radar-active' | 'ir';
  seekerFovDeg: number;
  seekerLockRangeM: number;
  maxFlightTimeS: number;
  minSpeedMs: number;
  fuseRadiusM: number;
  warheadYield: number;
  chaffSusceptibility: number;
  flareSusceptibility: number;
  /** IR seekers only: lock probability multiplier off the rear aspect. */
  rearAspectBias?: number;
}

/**
 * Missile flight (SPEC §3.6.1). Phase boundaries, the transonic drag rise, and the terms that
 * make a missile an object with an energy state rather than a homing token.
 */
export const MISSILE = {
  /** Ejection phase: no guidance, no motor. The smoke column starts here (SPEC §3.6.1). */
  launchPhaseS: 0.4,
  ejectionSpeedMs: 34,
  /** Ejection is canted up off the rail so the round is clear of the launcher before it lights. */
  ejectionCantDeg: 14,

  /** Terminal phase: the last stretch before impact, with higher gain and the fuse armed. */
  terminalTimeS: 1.5,
  terminalGainScale: 1.35,

  /**
   * Fraction of gravity the autopilot compensates.
   *
   * Full compensation, and it has to be: anything less leaves a constant downward acceleration
   * that proportional navigation can only partly correct against, and the residue shows up as a
   * systematic several-metre sag on *every* intercept. That bias then swamps the thing the whole
   * milestone is about — a 5 m miss the player did nothing to earn is indistinguishable from a
   * 5 m miss they broke hard for. Removing the sag is what makes the break turn legible.
   */
  gravityBiasFactor: 1.0,

  /**
   * Autopilot and airframe response lag, seconds — how long the fins take to build the g the
   * guidance law asked for.
   *
   * **This term is what makes the game work, and it is not optional.** SPEC §3.6.5 requires that a
   * break turn at 1.5–2.5 s to impact defeats the missile while the same break at 5 s does not.
   * With an instantaneous response that behaviour is unreachable: proportional navigation with
   * N > 2 corrects any target manoeuvre given time, and the only remaining defence would be
   * exceeding the missile's g limit — which a 90 m/s drone cannot do against a 28 g round. It is
   * the *lag* that converts "time remaining" into "correction possible", and it is why a late
   * break works and an early one is a gift. Real missiles have exactly this lag, for exactly this
   * reason.
   *
   * 0.45 s is the sluggish end of the tactical range, and command guidance earns it: the loop
   * runs through a ground station rather than an onboard seeker, so uplink and computation are
   * added to the airframe's own response. It puts a 2 s break at about four time constants —
   * enough margin for the manoeuvre to work, not so much that it always does.
   */
  autopilotLagS: 0.45,

  // --- Transonic drag. C_D rises sharply through Mach 1 and settles to a supersonic plateau.
  machDragPeak: 2.6, // multiplier on cd0 at the peak
  machDragPeakMach: 1.05,
  /**
   * Width of the transonic bump, in Mach. Narrow on purpose: the wave drag rise is genuinely
   * abrupt, and a wide curve would leave a subsonic missile dragging like a transonic one — at
   * Mach 0.6 the multiplier should be within a few percent of 1, not half way up the hill.
   */
  machDragWidth: 0.24,
  machDragSupersonic: 1.5, // plateau well above the peak

  /**
   * How long a seeker coasts on the last known target vector after losing lock, before going
   * ballistic (SPEC §3.6.3). A ballistic missile is blind, not harmless: its fuse still works.
   */
  lockMemoryS: 2.0,

  /** The fuse arms this long after launch, so a round cannot detonate on its own launcher. */
  fuseArmS: 0.8,

  /** Trail sample spacing, seconds. Finer than this and the ribbon costs more than it shows. */
  trailIntervalS: 0.045,
  /** Trail points retained per missile; at 0.045 s each this is about 5 s of smoke. */
  trailPoints: 112,

  /** Pool size. A two-site salvo of two is four; this leaves room for a bad day. */
  poolCapacity: 16,
} as const;

/**
 * Countermeasure behaviour (SPEC §3.7). The counts and durations are in `tuning.ts`; what a
 * decoy *does* is here.
 */
export const DECOY = {
  // --- Chaff
  /** Ejection speed relative to the aircraft, m/s, and how fast the cloud is left behind. */
  chaffEjectMs: 12,
  /** The bloom grows to its full radius over this long — a cloud is not instantly a cloud. */
  chaffBloomTimeS: 0.35,
  /**
   * Probability per second that a radar seeker inside a full bloom transfers lock, before the
   * missile's own `chaffSusceptibility` scales it. Per *second* rather than per release, so
   * lingering in the cloud helps and a single panicked release is not a guaranteed escape.
   */
  chaffDefeatPerS: 1.6,
  /** Chaff only works while the cloud is roughly between the missile and the aircraft. */
  chaffMaxSeparationDeg: 18,

  // --- Flares
  flareEjectMs: 16,
  /** Flares fall away and decelerate; this is how fast they shed the aircraft's velocity. */
  flareDragPerS: 1.1,
  flareFallMs2: 9.8,
  /**
   * Angular separation between flare and aircraft, seen from the missile, at which the seeker
   * can be fooled (SPEC §3.6.3). Below it the flare is inside the aircraft's own IR blob and
   * changes nothing — which is precisely why flares alone do not work and the player must turn.
   */
  flareDefeatPerS: 2.4,

  /** Decoys are pooled like everything else transient. */
  poolCapacity: 48,
} as const;

/**
 * Warhead effect (SPEC §3.6.4). `damage = baseDamage · (1 − d/r_fuse)^falloff · aspectFactor`,
 * where the aspect factor rewards a fragmentation pattern that catches the airframe broadside.
 */
export const WARHEAD = {
  /** Integrity removed by a direct centre hit at full yield. */
  baseDamage: 1.0,
  /** Damage multiplier at the worst (nose/tail-on) and best (beam) aspects. */
  aspectMin: 0.7,
  aspectMax: 1.15,
} as const;
